
import React, { useState, useMemo } from 'react';
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, ShoppingCart, Filter } from "lucide-react";
import type { OrderStoreItem } from '@/hooks/useOrderStoreItems';
import type { Location } from '@/types/entities';

interface StoreItemsSectionProps {
  isStorePurchase: boolean;
  selectedItems: Array<{ id: string; quantity: number }>;
  storeItems: OrderStoreItem[];
  storeItemsLoading: boolean;
  locations: Location[];
  currentRentalPoint: Location | null;
  onChange: (field: 'isStorePurchase', value: boolean) => void;
  onStoreItemChange: (id: string, quantity: number) => void;
  readOnly?: boolean;
}

export const StoreItemsSection: React.FC<StoreItemsSectionProps> = ({
  isStorePurchase,
  selectedItems,
  storeItems,
  storeItemsLoading,
  locations,
  currentRentalPoint,
  onChange,
  onStoreItemChange,
  readOnly = false
}) => {
  // State for filtering by current rental point
  const [showOnlyCurrentPoint, setShowOnlyCurrentPoint] = useState(false);

  const getItemQuantity = (id: string): number => {
    const item = selectedItems.find(item => item.id === id);
    return item ? item.quantity : 0;
  };

  // Helper function to get location name by ID
  const getRentalPointName = (rentalPointId: string): string => {
    const location = locations.find(loc => loc.id === rentalPointId);
    return location?.name || 'Неизвестная точка';
  };

  // Filter store items based on current rental point filter
  const filteredStoreItems = useMemo(() => {
    if (!showOnlyCurrentPoint || !currentRentalPoint) {
      return storeItems;
    }
    return storeItems.filter(item => item.rentalPointId === currentRentalPoint.id);
  }, [storeItems, showOnlyCurrentPoint, currentRentalPoint]);

  const handleToggleStorePurchase = (checked: boolean) => {
    if (readOnly) return; // no-op in read-only mode
    console.log('[StoreItemsSection] Toggle store purchase:', checked);
    onChange("isStorePurchase", checked);
    
    // Если отключаем покупку, очищаем выбранные товары
    if (!checked && selectedItems.length > 0) {
      selectedItems.forEach(item => {
        onStoreItemChange(item.id, 0);
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium flex items-center">
          <ShoppingCart className="mr-2 h-5 w-5 text-blue-600" />
          Покупка товаров
        </h3>
        <Switch 
          id="store-purchase-toggle" 
          checked={isStorePurchase} 
          onCheckedChange={handleToggleStorePurchase}
          disabled={readOnly}
        />
      </div>

      {isStorePurchase && (
        <div className="mt-4 space-y-4">
          {/* Filter controls */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Фильтры:</span>
            </div>
            <div className="flex items-center space-x-2">
              <Label htmlFor="current-point-filter" className="text-sm">
                Только в этом пункте
              </Label>
              <Switch
                id="current-point-filter"
                checked={showOnlyCurrentPoint}
                onCheckedChange={setShowOnlyCurrentPoint}
                disabled={readOnly}
              />
            </div>
          </div>

          {storeItemsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Загрузка товаров...</span>
            </div>
          ) : filteredStoreItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {showOnlyCurrentPoint ? (
                <>
                  <p>Нет товаров в текущем пункте аренды</p>
                  <p className="text-xs mt-2">Отключите фильтр, чтобы увидеть товары из других пунктов</p>
                </>
              ) : (
                <>
                  <p>Нет доступных товаров</p>
                  <p className="text-xs mt-2">Добавьте товары в магазин</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStoreItems.map((item) => {
                const isFromCurrentPoint = currentRentalPoint && item.rentalPointId === currentRentalPoint.id;
                return (
                  <div key={item.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                    isFromCurrentPoint ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{item.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isFromCurrentPoint 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {getRentalPointName(item.rentalPointId)}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {item.price}₽ (доступно: {item.availableQuantity})
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        max={item.availableQuantity}
                        value={getItemQuantity(item.id)}
                        onChange={(e) => onStoreItemChange(item.id, parseInt(e.target.value) || 0)}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        className={`w-20 px-2 py-1 text-sm border rounded ${readOnly ? 'bg-gray-100 text-muted-foreground' : ''}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
